import * as tslib_1 from "tslib";
import { Observable, pipe, of, BehaviorSubject } from 'rxjs';
import { switchMap, tap, map, catchError, filter, take, finalize } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { sortBy, extend } from 'lodash';
import { Page } from '@app/models/page';
import * as i0 from "@angular/core";
// eslint-disable-next-line no-shadow
export var CacheEvent;
(function (CacheEvent) {
    CacheEvent[CacheEvent["IDLE"] = 0] = "IDLE";
    CacheEvent[CacheEvent["UPDATE"] = 1] = "UPDATE";
})(CacheEvent || (CacheEvent = {}));
var Cache = /** @class */ (function () {
    function Cache() {
        this.tables = {};
        this.cacheTable = {};
        this.caches = {};
        window.cache = this;
    }
    Cache.prototype.register = function (name, model, expire) {
        this.tables[name] = {};
        this.cacheTable[name] = {
            n: name,
            t: 0,
            d: null,
            s: [],
        };
        this.caches[name] = {
            model: model,
            expire: expire || 600,
            loading: 0,
            changes: new BehaviorSubject(CacheEvent.IDLE),
        };
    };
    Cache.prototype.getTable = function (cache) {
        return this.tables[cache];
    };
    /**
     * Searches for and returns a model in the cache. If it does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param id The id of the model to get
     * @param resolver An observable that resolves to the model if it is not found
     */
    Cache.prototype.getOne = function (cache, id, resolver) {
        var _this = this;
        var tbl = this.getTable(cache);
        return of(tbl[id]).pipe(switchMap(function (results) {
            if (!results) {
                var cacheInfo_1 = _this.caches[cache];
                cacheInfo_1.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(_this.insertPipe(cache, resolver, null, cacheInfo_1), finalize(function () { return cacheInfo_1.changes.next(CacheEvent.IDLE); }), map(function () { return tbl[id]; }));
            }
            return of(results);
        }));
    };
    /**
     * Searches for and returns multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    Cache.prototype.getSome = function (cache, ids, resolver) {
        var _this = this;
        var models = [];
        var noResult = false;
        var cacheQueryFn = function () {
            models = [];
            for (var i = 0; i < ids.length; i++) {
                var val = _this.getTable(cache)[ids[i]];
                models.push(val);
                if (!val) {
                    noResult = true;
                }
            }
        };
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(switchMap(function () {
            cacheQueryFn();
            if (noResult) {
                var cacheInfo_2 = _this.caches[cache];
                cacheInfo_2.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(_this.insertPipe(cache, resolver, null, cacheInfo_2), finalize(function () { return cacheInfo_2.changes.next(CacheEvent.IDLE); }), map(function () {
                    cacheQueryFn();
                    return models;
                }));
            }
            return of(models);
        }));
    };
    /**
     * Searches for and joins multiple models in the cache. If one does not exist
     * in the cache, subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param ids The ids of the models
     * @param resolver An observable that resolves to the model if it is not found
     */
    Cache.prototype.joinSome = function (cache, models, idField, joinField, resolver) {
        var _this = this;
        var noResult = false;
        var cacheQueryFn = function () {
            for (var i = 0; i < models.length; i++) {
                var val = void 0;
                if (!models[i][joinField]) {
                    val = _this.getTable(cache)[models[i][idField]];
                    models[i][joinField] = val;
                }
                if (!val) {
                    noResult = true;
                }
            }
        };
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(switchMap(function () {
            cacheQueryFn();
            if (noResult) {
                var cacheInfo_3 = _this.caches[cache];
                cacheInfo_3.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(_this.insertPipe(cache, resolver, null, cacheInfo_3), finalize(function () { return cacheInfo_3.changes.next(CacheEvent.IDLE); }), map(function () {
                    cacheQueryFn();
                    return models;
                }));
            }
            return of(models);
        }));
    };
    Cache.prototype.getPage = function (cache, page, resolver) {
        var _this = this;
        var cacheInfoQuery = this.cacheTable[cache];
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(map(function () { return cacheInfoQuery; }), this.checkForPage(page, this.caches[cache]), switchMap(function (data) {
            if (!data.page) {
                var cacheInfo_4 = _this.caches[cache];
                cacheInfo_4.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(_this.insertPipe(cache, resolver, data.info, _this.caches[cache]), finalize(function () { return cacheInfo_4.changes.next(CacheEvent.IDLE); }), map(function (d) { return d.page; }));
            }
            return of(data.page);
        }), map(function (cachePage) { return _this.getPageQuery(cache, page, cachePage); }));
    };
    /**
     * Returns all of the models in the cache. If the cache is expired,
     * subscribes to the provided resolver and caches the result.
     * @param cache The name of the cache (maps to the database collection name)
     * @param resolver An observable that resolves to the model if it is not found
     * @param sortField The field to sort the data on
     * @param sortDir The sort direction
     */
    Cache.prototype.getAll = function (cache, resolver, sortField, sortDir) {
        var _this = this;
        var cacheInfoQuery = this.cacheTable[cache];
        return this.waitUntilCacheIdle(this.caches[cache]).pipe(map(function () { return cacheInfoQuery; }), switchMap(function (results) {
            if (_this.cacheExpired(results)) {
                var cacheInfo_5 = _this.caches[cache];
                cacheInfo_5.changes.next(CacheEvent.UPDATE);
                return resolver.req.pipe(_this.insertPipe(cache, resolver, results, cacheInfo_5), finalize(function () { return cacheInfo_5.changes.next(CacheEvent.IDLE); }));
            }
            return of(null);
        }), map(function () {
            var models = sortBy(_this.getTable(cache), sortField || 'id');
            if (sortDir === 'DESC') {
                models = models.reverse();
            }
            return models;
        }));
    };
    Cache.prototype.put = function (cache, models) {
        var e_1, _a;
        var cacheInfo = this.caches[cache];
        cacheInfo.changes.next(CacheEvent.UPDATE);
        var tbl = this.getTable(cache);
        try {
            for (var models_1 = tslib_1.__values(models), models_1_1 = models_1.next(); !models_1_1.done; models_1_1 = models_1.next()) {
                var model = models_1_1.value;
                tbl[model.id] = model;
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (models_1_1 && !models_1_1.done && (_a = models_1.return)) _a.call(models_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        cacheInfo.changes.next(CacheEvent.IDLE);
        return of(null);
    };
    Cache.prototype.mapToClassPipe = function (cache) {
        var _this = this;
        return pipe(map(function (models) {
            var e_2, _a, e_3, _b, e_4, _c;
            if (Array.isArray(models)) {
                try {
                    for (var models_2 = tslib_1.__values(models), models_2_1 = models_2.next(); !models_2_1.done; models_2_1 = models_2.next()) {
                        var obj = models_2_1.value;
                        var model = Object.create(_this.caches[cache].model);
                        try {
                            for (var obj_1 = tslib_1.__values(obj), obj_1_1 = obj_1.next(); !obj_1_1.done; obj_1_1 = obj_1.next()) {
                                var m = obj_1_1.value;
                                if (obj.hasOwnProperty(m)) {
                                    try {
                                        model[m] = obj[m];
                                    }
                                    catch (_) { }
                                }
                            }
                        }
                        catch (e_3_1) { e_3 = { error: e_3_1 }; }
                        finally {
                            try {
                                if (obj_1_1 && !obj_1_1.done && (_b = obj_1.return)) _b.call(obj_1);
                            }
                            finally { if (e_3) throw e_3.error; }
                        }
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (models_2_1 && !models_2_1.done && (_a = models_2.return)) _a.call(models_2);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
            else if (typeof models === 'object') {
                var model = Object.create(_this.caches[cache].model);
                try {
                    for (var models_3 = tslib_1.__values(models), models_3_1 = models_3.next(); !models_3_1.done; models_3_1 = models_3.next()) {
                        var m = models_3_1.value;
                        if (models.hasOwnProperty(m)) {
                            try {
                                model[m] = models[m];
                            }
                            catch (_) { }
                        }
                    }
                }
                catch (e_4_1) { e_4 = { error: e_4_1 }; }
                finally {
                    try {
                        if (models_3_1 && !models_3_1.done && (_c = models_3.return)) _c.call(models_3);
                    }
                    finally { if (e_4) throw e_4.error; }
                }
            }
        }));
    };
    Cache.prototype.insertPipe = function (cache, resolver, cacheInfo, cacheLocalInfo) {
        var _this = this;
        return pipe(tap(function (data) {
            var e_5, _a, e_6, _b;
            var models = [];
            cacheLocalInfo.changes.next(CacheEvent.UPDATE);
            try {
                // Create and populate new models from API data
                for (var _c = tslib_1.__values(data.data), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var obj = _d.value;
                    models.push(new cacheLocalInfo.model(obj));
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_5) throw e_5.error; }
            }
            // If the request is for all models, clear the table
            // and add new models. Otherwise, put all models into
            // table
            if (resolver.params && resolver.params.all) {
                _this.tables[cache] = {};
            }
            try {
                for (var models_4 = tslib_1.__values(models), models_4_1 = models_4.next(); !models_4_1.done; models_4_1 = models_4.next()) {
                    var model = models_4_1.value;
                    _this.tables[cache][model.id] = model;
                }
            }
            catch (e_6_1) { e_6 = { error: e_6_1 }; }
            finally {
                try {
                    if (models_4_1 && !models_4_1.done && (_b = models_4.return)) _b.call(models_4);
                }
                finally { if (e_6) throw e_6.error; }
            }
            if (cacheInfo) {
                var cacheUpdate = {};
                if (resolver.params && resolver.params.all) {
                    cacheUpdate.d = DateTime.local().toISO();
                    cacheUpdate.t = data.count;
                }
                else if (resolver.page) {
                    resolver.page.cursor = models[0][resolver.page.sortField];
                    cacheUpdate.s = _this.filterSections(cache, cacheInfo).s;
                    cacheUpdate.s.push({
                        d: DateTime.utc().toISO(),
                        p: resolver.page.serialize(),
                    });
                }
                _this.cacheTable[cacheInfo.n] = extend(_this.cacheTable[cacheInfo.n], cacheUpdate);
            }
        }), map(function () { return resolver; }), tap(function () { return cacheLocalInfo.changes.next(CacheEvent.IDLE); }), catchError(function (val) {
            cacheLocalInfo.changes.next(CacheEvent.IDLE);
            throw val;
        }));
    };
    Cache.prototype.waitUntilCacheIdle = function (cache) {
        return cache.changes.pipe(filter(function (change) { return change === CacheEvent.IDLE; }), take(1));
    };
    Cache.prototype.waitUntilCacheIdlePipe = function (cache) {
        var _this = this;
        return pipe(switchMap(function (data) { return _this.waitUntilCacheIdle(cache).pipe(map(function () { return data; })); }));
    };
    Cache.prototype.cacheExpired = function (data) {
        var cache = this.caches[data.n];
        if (!data.d ||
            (data.d &&
                cache.expire !== Infinity &&
                DateTime.fromISO(data.d).plus({ seconds: cache.expire }) < DateTime.local())) {
            return true;
        }
        return false;
    };
    Cache.prototype.checkForPage = function (page, localCache) {
        return pipe(map(function (cacheInfo) {
            // Check if entire collection is fetched
            if (cacheInfo.d &&
                DateTime.fromISO(cacheInfo.d) > DateTime.local().minus({ seconds: localCache.expire })) {
                return {
                    page: new Page(1, Infinity, page.sortField, page.sortDirection),
                    info: cacheInfo,
                };
            }
            // Find a valid page (either matching or a page that encompasses the input page)
            var match = cacheInfo.s.find(function (section) { return section.p && page.compare(Page.deserialize(section.p)); });
            return {
                page: match && match.p && Page.deserialize(match.p),
                info: cacheInfo,
            };
        }));
    };
    Cache.prototype.getPageQuery = function (cache, page, cachePage) {
        var offset = (page.page - cachePage.page) * page.pageSize || 0;
        if (cachePage.cursor) {
            if (page.sortDirection === 'ASC') {
                var filtered = Object.values(this.getTable(cache)).filter(function (model) { return model[page.sortField] >= cachePage.cursor; });
                return sortBy(filtered, page.sortField).slice(offset, offset + page.limit);
            }
            else {
                var filtered = Object.values(this.getTable(cache)).filter(function (model) { return model[page.sortField] <= cachePage.cursor; });
                return sortBy(filtered, page.sortField)
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        }
        else {
            if (page.sortDirection === 'ASC') {
                return Object.values(this.getTable(cache)).slice(offset, offset + page.limit);
            }
            else {
                return Object.values(this.getTable(cache))
                    .reverse()
                    .slice(offset, offset + page.limit);
            }
        }
    };
    Cache.prototype.filterSections = function (cache, cacheInfo) {
        var _this = this;
        // eslint-disable-next-line max-len
        cacheInfo.s = cacheInfo.s.filter(function (section) { return DateTime.fromISO(section.d) > DateTime.local().minus({ second: _this.caches[cache].expire }); });
        return cacheInfo;
    };
    Cache.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function Cache_Factory() { return new Cache(); }, token: Cache, providedIn: "root" });
    return Cache;
}());
export { Cache };
